import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useThemePreference } from '../hooks/useUIPreferences';

type Theme = 'light' | 'dark';
type ThemePreference = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  themePreference: ThemePreference;
  toggleTheme: () => void;
  setThemePreference: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { theme: themePreference, setTheme: setThemePreference, isLoaded } = useThemePreference();

  // Determine actual theme based on preference and system setting
  const theme = useMemo<Theme>(() => {
    if (themePreference === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return themePreference;
  }, [themePreference]);

  // Listen to system theme changes when preference is 'system'
  useEffect(() => {
    if (themePreference !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      // Force re-render by updating a dummy state if needed
      // The useMemo above will automatically recalculate
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference]);

  // Update document class when theme changes
  useEffect(() => {
    if (!isLoaded) return; // Don't apply theme until preferences are loaded
    
    const root = window.document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme, isLoaded]);

  const toggleTheme = () => {
    const nextTheme: ThemePreference = themePreference === 'light' ? 'dark' : 
                                       themePreference === 'dark' ? 'system' : 'light';
    setThemePreference(nextTheme);
  };

  const value = {
    theme,
    themePreference,
    toggleTheme,
    setThemePreference,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}